import { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { isFunction } from 'lodash';
import { AnimatePresence, motion } from 'framer-motion';

import DropArrowIcon from 'components/icons/DropArrowIcon';
import DropArrowBigIcon from 'components/icons/DropArrowBigIcon';
import CheckedIcon from 'components/icons/CheckedIcon';
import useClickAway from 'lib/hooks/use-click-away';
import { fadeInOut } from 'lib/framer-motion/fade-in-out';
import { getByValue } from 'utils';

import { OptionType } from 'types';

import {
  StyledDropdownWrapper,
  StyledOptions,
  StyledLabel,
  StyledList,
  StyledListItem
} from './dropdown.style';

type VariantNames = 'default' | 'small';

type DropdownProps = {
  name?: string;
  value?: string;
  label?: string;
  disabled?: boolean;
  variant?: VariantNames;
  options: OptionType[];
  error?: boolean;
  onChange: (e: {
    target: {
      name: string;
      value: string;
    };
  }) => void;
};

const Dropdown = ({
  name = '',
  value = '',
  label = '',
  disabled = false,
  variant = 'default',
  options,
  error = false,
  onChange = () => {}
}: DropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<OptionType | null>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const handleToggle = () => setIsOpen((r) => !r);

  const onClickHandler = (item: OptionType) => {
    setSelectedOption(item);
    if (isFunction(onChange)) {
      onChange({ target: { name, value: item.value } });
    }
    setIsOpen(() => false);
  };

  useEffect(() => {
    setSelectedOption(getByValue(options, value));
  }, []);

  useClickAway(dropdownRef, () => setIsOpen(() => false));

  return (
    <StyledDropdownWrapper
      className={cx(variant, {
        'is--disabled': disabled,
        'is--open': isOpen,
        error
      })}
      ref={dropdownRef}
    >
      <StyledOptions
        className={cx(variant, {
          open: isOpen
        })}
        onClick={!disabled ? handleToggle : undefined}
      >
        {variant === 'small' && <DropArrowBigIcon />}
        <StyledLabel>
          {selectedOption !== null ? selectedOption?.label : label}
        </StyledLabel>
        {variant === 'default' && <DropArrowIcon />}
      </StyledOptions>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial="from"
            animate="to"
            exit="from"
            variants={fadeInOut(0.1)}
          >
            <StyledList>
              {options.length > 0 ? (
                options.map((item) => (
                  <StyledListItem
                    key={item.value}
                    className={cx({
                      active:
                        selectedOption !== null &&
                        selectedOption?.value === item.value
                    })}
                    onClick={() =>
                      item.value !== selectedOption.value
                        ? onClickHandler(item)
                        : setIsOpen(() => false)
                    }
                  >
                    <span>{item.label}</span>
                    {variant === 'default' &&
                      selectedOption !== null &&
                      selectedOption?.value === item.value && (
                        <CheckedIcon color="#fff" />
                      )}
                  </StyledListItem>
                ))
              ) : (
                <StyledListItem>No options</StyledListItem>
              )}
            </StyledList>
          </motion.div>
        )}
      </AnimatePresence>
    </StyledDropdownWrapper>
  );
};

export default Dropdown;
